import { useEffect, useState } from "react";
import Seo from "@/components/base/Seo";
import ApiState from "@/components/base/ApiState";
import { SITE } from "@/config/site";
import { OG_IMAGES } from "@/config/ogImages";
import { popularSearches } from "@/mocks/categories";
import {
  getBusinesses,
  getCategories,
  getFeaturedBusinesses,
  getLocations,
  getSiteStats,
  getTestimonials,
  getTouristSpots,
} from "@/services/publicApi";
import type { Business, Category, Location, TouristSpot } from "@/types";
import Hero from "./components/Hero";
import SponsoredBusinesses from "./components/SponsoredBusinesses";
import FeaturedListings from "./components/FeaturedListings";
import RecentlyAdded from "./components/RecentlyAdded";
import CategoryGrid from "./components/CategoryGrid";
import TestimonialsAndFaq from "./components/TestimonialsAndFaq";
import TouristTeaser from "./components/TouristTeaser";

export default function Home() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [featured, setFeatured] = useState<Business[]>([]);
  const [all, setAll] = useState<Business[]>([]);
  const [stats, setStats] = useState<{ id: string; value: string; label: string }[]>([]);
  const [testimonials, setTestimonials] = useState<
    { id: string; name: string; role: string; rating: number; text: string }[]
  >([]);
  const [spots, setSpots] = useState<TouristSpot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    setError(null);
    Promise.all([
      getCategories(),
      getLocations(),
      getFeaturedBusinesses(),
      getBusinesses(),
      getSiteStats(),
      getTestimonials(),
      getTouristSpots(),
    ])
      .then(([c, l, f, a, st, t, sp]) => {
        if (!active) return;
        setCategories(c);
        setLocations(l);
        setFeatured(f);
        setAll(a);
        // The API returns both the original and demo stat sets; the hero is
        // built for four cards, so keep the first four (the original set).
        setStats(st.slice(0, 4));
        setTestimonials(t);
        setSpots(sp);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Something went wrong while loading the page.");
      });
    return () => {
      active = false;
    };
  }, [reloadToken]);

  const sponsored = all.filter((b) => b.plan === "Premium");
  const recent = all.slice().reverse();

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.url,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE.url}/directory?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <Seo
        title="Local Business Directory for Mysore, Mandya, Chamarajanagar & Kodagu"
        description="Search and discover trusted local businesses across the Mysore region — restaurants, clinics, hotels, shopping, home services and more."
        keywords="Mysore Yellow Pages, business directory Mysore, local services Mysore, Mandya listings, Kodagu directory"
        path="/"
        image={OG_IMAGES.home}
        jsonLd={orgJsonLd}
      />
      <Hero categories={categories} locations={locations} popularSearches={popularSearches} stats={stats} />
      {error ? (
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-16 md:py-24">
          <ApiState loading={false} error={error} onRetry={() => setReloadToken((n) => n + 1)} />
        </div>
      ) : (
        <>
          <SponsoredBusinesses businesses={sponsored} />
          <FeaturedListings businesses={featured} />
          <RecentlyAdded businesses={recent} />
          <CategoryGrid categories={categories} />
          <TestimonialsAndFaq testimonials={testimonials} />
          <TouristTeaser spots={spots} />
        </>
      )}
    </>
  );
}